
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { ConversationList } from "@/components/inbox/ConversationList";
import { useRealtimeMessages } from "@/hooks/useRealtimeMessages";

const Inbox = () => {
  const navigate = useNavigate();
  const [userId, setUserId] = useState<string | null>(null);
  const queryClient = useQueryClient();

  // Get current user
  useEffect(() => {
    const fetchUser = async () => {
      const { data } = await supabase.auth.getUser();
      setUserId(data.user?.id || null);
    };
    fetchUser();
  }, []);

  // Enable real-time updates for messages
  useRealtimeMessages(null, userId, false);

  const { data: conversations, isLoading } = useQuery({
    queryKey: ['conversations'],
    queryFn: async () => {
      if (!userId) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('conversations')
        .select(`
          id,
          unread_count,
          updated_at,
          cook_id,
          foodie_id,
          cook:profiles!conversations_cook_id_fkey(
            id,
            first_name,
            last_name,
            image_url
          ),
          foodie:profiles!conversations_foodie_id_fkey(
            id,
            first_name,
            last_name,
            image_url
          ),
          meal:meals(
            id,
            name,
            image
          ),
          messages(
            content,
            created_at,
            sender_id
          )
        `)
        .or(`cook_id.eq.${userId},foodie_id.eq.${userId}`)
        .order('updated_at', { ascending: false });

      if (error) throw error;

      console.log('Inbox - Fetched conversations:', data.length);
      data.forEach(conv => {
        console.log(`Conversation ${conv.id} has unread count: ${conv.unread_count}`);
      });

      return data.map((conv) => ({
        ...conv,
        last_message: conv.messages.sort(
          (a: any, b: any) =>
            new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        )[0] || null,
      }));
    },
    enabled: !!userId,
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: true,
    refetchInterval: 5000 // Force refetch every 5 seconds to ensure unread counts are up-to-date
  });

  const handleConversationSelect = async (id: string) => {
    if (userId) {
      navigate(`/inbox/${id}`);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <main className="px-[16px] py-[16px]">
        <ConversationList 
          conversations={conversations || []} 
          onSelectConversation={handleConversationSelect}
          currentUserId={userId || ''}
          isLoading={isLoading}
        />
      </main>
    </div>
  );
};

export default Inbox;
